/**
 * SAMPLE DATA for screens whose server operations other streams are still building.
 * Everything here is badged "sample" on screen (SampleBadge). Each function names the operation
 * that replaces it; when that operation merges, swap the function body for an api() call and
 * delete the sample. Nothing outside this file invents data (CONTRACTS §11).
 */
import { api, ApiError, qs } from "./api";
import type { Broadcast, BroadcastPreview, EmailDoc, Segment, SegmentCounts, SegmentDefinition } from "./types";
import { segmentToFilter } from "./segments";
import { filterParams } from "@/features/contacts/listSearch";

export const pendingModules = {
  billing: "crm-billing",
  automations: "crm-automations",
  events: "crm-automations",
  email: "crm-email",
  logins: "crm-sites",
} as const;

/** True when the server has the operation (GET /api/v1/ops lists them); lets screens switch from
 *  sample to real data as soon as a stream's work is merged, without a UI change. */
interface OpInfo {
  name: string;
  method?: string;
  path?: string;
}
let opsCache: Promise<Map<string, OpInfo>> | null = null;
function ops(): Promise<Map<string, OpInfo>> {
  opsCache ??= api<{ ops?: OpInfo[]; operations?: OpInfo[] }>("/api/v1/ops")
    .then((r) => new Map((r.ops ?? r.operations ?? []).map((o) => [o.name, o])))
    .catch(() => new Map<string, OpInfo>());
  return opsCache;
}
export function hasOp(name: string): Promise<boolean> {
  return ops().then((m) => m.has(name));
}

/** Calls an operation by name with its registered method and path, for ops whose REST path another stream picks:
 *  {param} path parts come from input; the rest goes in the query (GET) or the JSON body. */
export async function callOp<T>(name: string, input: Record<string, string | number | boolean | undefined> = {}): Promise<T> {
  const op = (await ops()).get(name);
  if (!op?.path) throw new ApiError(404, "not_found", `The server has no ${name} operation yet.`);
  const rest = { ...input };
  const path = op.path.replace(/\{([a-z_]+)\}/g, (_, k: string) => {
    const v = rest[k] ?? (k === "id" ? (rest.product_id ?? rest.contact_id) : undefined);
    delete rest[k];
    if (k === "id") delete rest.product_id;
    return encodeURIComponent(String(v ?? ""));
  });
  const method = (op.method ?? "GET").toUpperCase();
  if (method === "GET") return api<T>(path + qs(rest));
  return api<T>(path, { method, json: rest });
}

/** Calls a real operation if it exists, otherwise returns the sample (marked). */
async function realOrSample<T>(op: string, real: () => Promise<T>, sample: () => T): Promise<{ data: T; sample: boolean }> {
  if (await hasOp(op)) {
    try {
      return { data: await real(), sample: false };
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 404) throw e;
    }
  }
  return { data: sample(), sample: true };
}

// ---------------------------------------------------------------------------------------------
// Billing (crm-billing): billing_contact_summary (GET /api/v1/contacts/{id}/billing) and
// billing_contact_detail (GET /api/v1/contacts/{id}/billing/detail), merged 2026-10-26. Shapes from
// internal/billing/contact_billing.go. The sample below is only used against an older server.

export interface BillingCard {
  id?: number;
  brand: string;
  last4: string;
  exp: string;
  gateway: string;
  default?: boolean;
  /** active, expired, failed, removed */
  status?: string;
  /** Stripe wallet (apple_pay, google_pay, link, amazon_pay, cashapp); Link, Amazon Pay and Cash App Pay have no last 4. */
  wallet?: string | null;
  /** The customer in Stripe's dashboard (test or live), built by billing. */
  dashboard_url?: string | null;
}

export interface BillingSummary {
  next_charge: { date: string; amount_cents: number; plan: string } | null;
  card: BillingCard | null;
  open_invoices: number;
  overdue_cents: number;
  /** A 7-day sign-in link for the CUSTOMER (portal, card page). Copy it to send; never open it as staff. */
  update_card_url: string | null;
  pending_approvals?: number;
  ltv_cents?: number;
  /** Chargebacks still open on their payments (billing records Stripe's charge.dispute.* events). */
  open_disputes?: number;
  /** Their customer page in Stripe's dashboard. */
  stripe_customer_url?: string | null;
}

export interface BillingDetail {
  subscriptions: {
    id: number;
    plan: string;
    status: string;
    amount_cents: number;
    interval: string;
    next_charge_on: string | null;
    started_on: string;
    ends_on?: string | null;
    /** false = the CRM doesn't charge it (imported from Keap, switched on at the final cutover) */
    billing_enabled?: boolean;
  }[];
  orders: { id: number; placed_at: string; description: string; total_cents: number; status: string; brand_id: number; source?: string }[];
  invoices: { id?: number; number: string; due_on: string | null; total_cents: number; balance_cents: number; status: string; description?: string; pay_url?: string }[];
  payments: {
    id: number;
    at: string;
    amount_cents: number;
    refunded_cents?: number;
    /** billing's own label ("Visa ending 4242"); the parts below, when sent, are used instead. */
    method: string;
    status: string;
    message?: string;
    invoice_id?: number;
    gateway?: string;
    card_brand?: string | null;
    last4?: string | null;
    wallet?: string | null;
    dashboard_url?: string | null;
    dispute_status?: string | null;
    disputed_at?: string | null;
    dispute_reason?: string | null;
    /** Why it was charged, in one plain sentence (billing_charge_explain). */
    sentence?: string;
  }[];
  cards: BillingCard[];
  stripe_customer_url?: string | null;
  access?: { key: string; until?: string | null; sources: string[] }[];
}

export function getBillingSummary(contactId: number) {
  return realOrSample<BillingSummary>(
    "billing_contact_summary",
    () => api<BillingSummary>(`/api/v1/contacts/${contactId}/billing`),
    () =>
      contactId % 37 === 0
        ? {
            next_charge: { date: "2026-11-15", amount_cents: 9900, plan: "Investor's Club (monthly)" },
            card: { brand: "Visa", last4: "4242", exp: "08/28", gateway: "Authorize.net" },
            open_invoices: 0,
            overdue_cents: 0,
            update_card_url: null,
          }
        : { next_charge: null, card: null, open_invoices: 0, overdue_cents: 0, update_card_url: null },
  );
}

export function getBillingDetail(contactId: number) {
  return realOrSample<BillingDetail>(
    "billing_contact_detail",
    () => api<BillingDetail>(`/api/v1/contacts/${contactId}/billing/detail`),
    () => ({
      subscriptions:
        contactId % 37 === 0
          ? [{ id: 5001, plan: "Investor's Club", status: "active", amount_cents: 9900, interval: "month", next_charge_on: "2026-11-15", started_on: "2025-03-15" }]
          : [],
      orders: [
        { id: 100231, placed_at: "2026-09-03T16:20:00Z", description: "How to Buy a Mobile Home Park (book)", total_cents: 2995, status: "paid", brand_id: 2 },
        { id: 79012, placed_at: "2025-11-28T18:02:00Z", description: "Mobile Home Park Boot Camp (Dallas)", total_cents: 249700, status: "paid", brand_id: 2 },
      ],
      invoices: [{ number: "INV-100231", due_on: "2026-09-03", total_cents: 2995, balance_cents: 0, status: "paid" }],
      payments: [{ id: 10000412, at: "2026-09-03T16:20:41Z", amount_cents: 2995, method: "Visa ending 4242", status: "succeeded" }],
      cards: [{ brand: "Visa", last4: "4242", exp: "08/28", gateway: "Authorize.net", default: true }],
    }),
  );
}

// ---------------------------------------------------------------------------------------------
// Automations (crm-automations): automations_contact_positions (GET /api/v1/contacts/{id}/automations),
// merged 2026-10-26. The sample below is only used against an older server.

export interface AutomationPosition {
  enrollment_id: number;
  automation_id: number;
  automation_name: string;
  automation_status: string;
  version: number;
  status: "active" | "waiting" | "paused" | "completed" | "exited" | string;
  shadow: boolean;
  step_label: string;
  step_number: number;
  step_count: number;
  summary: string;
  next_run_at: string | null;
  entered_at: string;
  completed_at: string | null;
  exit_reason: string | null;
}

export function getAutomationPositions(contactId: number) {
  return realOrSample<AutomationPosition[]>(
    "automations_contact_positions",
    () => api<{ positions: AutomationPosition[] }>(`/api/v1/contacts/${contactId}/automations`).then((r) => r.positions),
    () =>
      contactId % 3 === 0
        ? [
            { enrollment_id: 1, automation_id: 12, automation_name: "MHU welcome series", automation_status: "live", version: 3, status: "waiting", shadow: false, step_label: "How to value a park", step_number: 3, step_count: 6, summary: "Step 3 of 6: email “How to value a park”", next_run_at: "2026-10-28T13:00:00Z", entered_at: "2026-10-20T15:02:00Z", completed_at: null, exit_reason: null },
          ]
        : [],
  );
}

// ---------------------------------------------------------------------------------------------
// Events (crm-automations): events_contact_upcoming (GET /api/v1/contacts/{id}/events), merged 2026-10-26.
// The sample below is only used against an older server.

export interface UpcomingEvent {
  registration_id: number;
  event_id: number;
  event: string;
  kind: string;
  brand_id: number;
  session_id: number | null;
  starts_at: string | null;
  ends_at: string | null;
  when: string;
  location: string;
  status: "registered" | "waitlisted" | "attended" | "no_show" | "cancelled" | string;
  guests: number;
  join_url: string | null;
  checked_in_at: string | null;
}

export function getUpcomingEvents(contactId: number) {
  return realOrSample<UpcomingEvent[]>(
    "events_contact_upcoming",
    () => api<{ registrations: UpcomingEvent[] }>(`/api/v1/contacts/${contactId}/events`).then((r) => r.registrations),
    () =>
      contactId % 4 === 0
        ? [{ registration_id: 801, event_id: 1, event: "Mobile Home Park Boot Camp", kind: "boot_camp", brand_id: 2, session_id: 1, starts_at: "2026-12-04T15:00:00Z", ends_at: null, when: "Fri, Dec 4, 2026 8:00 AM MST", location: "Dallas, TX", status: "registered", guests: 1, join_url: null, checked_in_at: null }]
        : [],
  );
}

// ---------------------------------------------------------------------------------------------
// Email engine (crm-email): segments_*, email_*, broadcasts_*. Until those operations are merged,
// the screens keep their work in THIS BROWSER only (localStorage), clearly badged as sample.
// Counts use the real contacts list where the rules allow (segmentToFilter).


interface SampleStore {
  segments: Segment[];
  emails: EmailDoc[];
  broadcasts: Broadcast[];
  next: number;
}

const STORE_KEY = "crm-sample-email-v1";

export function sampleStore(): SampleStore {
  try {
    const s = JSON.parse(localStorage.getItem(STORE_KEY) ?? "null") as SampleStore | null;
    if (s && Array.isArray(s.segments)) return s;
  } catch {
    /* private mode or corrupt: start empty */
  }
  return { segments: [], emails: [], broadcasts: [], next: 900001 };
}

export function saveSampleStore(s: SampleStore) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

/** Sample count: real numbers from contacts_list when the rules map onto its filters. */
export async function sampleCount(d: SegmentDefinition): Promise<SegmentCounts | null> {
  const f = segmentToFilter(d);
  if (!f) return null;
  const total = (extra: Record<string, unknown>) =>
    api<{ total?: number }>("/api/v1/contacts" + qs({ ...filterParams({ ...f, ...extra }), with_total: true, limit: 1 })).then((r) => r.total ?? 0);
  const start = performance.now();
  const [matching, mailable] = await Promise.all([total({}), total({ email_status: "mailable" })]);
  return { matching, mailable, no_mailable_address: matching - mailable, unsubscribed_brand: 0, skip_marketing_tags: 0, ms: Math.round(performance.now() - start) };
}

export function samplePreview(b: Broadcast, e: EmailDoc | undefined, counts: SegmentCounts | null, sendAt: string): BroadcastPreview {
  const n = counts?.mailable ?? 0;
  return {
    broadcast_id: b.id,
    name: b.name,
    brand: String(b.brand_id),
    subject: e?.version.subject ?? "",
    from: e ? (e.version.from_email ? `${e.version.from_name} <${e.version.from_email}>` : `${e.version.from_name} (the brand's usual address)`) : "",
    email_id: e?.id ?? 0,
    email_version: e?.version.version ?? 1,
    audience: b.segment_id ? `segment #${b.segment_id}` : "rules",
    counts: counts ?? { matching: 0, mailable: 0, no_mailable_address: 0, unsubscribed_brand: 0, skip_marketing_tags: 0, ms: 0 },
    warmup_stage: 0,
    will_queue: n,
    held_for_warmup: 0,
    today_cap_left: -1,
    estimated_days: 1,
    estimated_hours_at_full_rate: Math.round((n / (14 * 0.9) / 3600) * 100) / 100,
    send_at_mountain: sendAt,
    confirm_required: true,
    warnings: ["Sample only: nothing is sent until the email module is merged."],
  };
}
